import { Component, OnDestroy } from '@angular/core';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { ViewSalesflowComponent } from 'components/task/salesflow/view-salesflow.component';
import { CredentialType } from 'enums/CredentialType';
import { TaskflowStatus } from 'enums/TaskflowStatus';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { CompanyService } from 'services/company.service';
import { CredentialRequired } from 'services/credential.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';
import { CompanyInformationComponent } from './company-information.component';
import { ListContactComponent } from './contact/list-contact.component';

@Component({
	selector: 'company-salesflow',
	templateUrl: './company-salesflow.component.html',
	imports: [Layout, ListView, ViewSalesflowComponent, CompanyInformationComponent, ListContactComponent, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('company-salesflow')
@CredentialRequired(CredentialType.BetaUserAccess)
export class ViewCompanySalesflowComponent implements OnDestroy {

	credentialType = CredentialType;
	public selectedTaskflowId?: string;
	public openSalesflows: TaskflowModel[] = [];
	public closedSalesflows: TaskflowModel[] = [];
	private onTaskflowChanges: Subscription;
	private _companyId = '';
	public company = new CompanyModel();
	loading = true;

	@UrlParam
	set companyId(id: string) {
		if (id) {
			this._companyId = id;
			this.getSalesflows();
			this.companyService.getCompany(id).then((company) => {
				this.company = company;
			});
			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent)
				.then(viewCompany => { viewCompany.companyId = id; });
		}
	}
	get companyId() {
		return this._companyId;
	}

	constructor(private readonly taskService: TaskService,
		private readonly navigation: NavigationService,
		private readonly translate: TranslatePipe,
		private readonly pushService: PushService,
		private readonly userService: UserService,
		private readonly companyService: CompanyService) {

		this.onTaskflowChanges = this.pushService.onTaskflowChanges.subscribe(event => {
			if (this.openSalesflows
				.filter(r => r.identifierObject === TaskflowModel)
				.filter(r => r.identifierId === event.data.taskflowId).length > 0 ||
				this.closedSalesflows
					.filter(r => r.identifierObject === TaskflowModel)
				.filter(r => r.identifierId === event.data.taskflowId).length > 0 ||
				event.data.companyId == this.companyId) {
				this.getSalesflows();
			}
		});
	}

	getSalesflows() {
		this.loading = true;
		this.taskService.getCompanySalesflows(this.companyId).then(taskflows => {
			this.loading = false;
			this.openSalesflows = [];
			this.closedSalesflows = [];
			taskflows.forEach((taskflow: TaskflowModel) => {
				if (!taskflow) return;
				if (taskflow.status == TaskflowStatus.Active) this.openSalesflows.push(taskflow);
				if (taskflow.status == TaskflowStatus.Completed) this.closedSalesflows.push(taskflow);
			});

			if (this.selectedTaskflowId == undefined && this.openSalesflows.length > 0) {
				this.selectedTaskflowId = this.openSalesflows[0].identifierId;
			}
		});
	}

	getStatusColor(taskflow: TaskflowModel) {
		let taskflowCommunicationLogs = this.getAllCommunicationLogsOnTaskflow(taskflow);
		if (taskflowCommunicationLogs[0] && taskflowCommunicationLogs[0].statusType) {
			return this.taskService.getLogEntryStatusColor(taskflowCommunicationLogs[0].statusType, true);
		}
	}

	getOwner(taskflow: TaskflowModel) {
		let CommunicationLogs = this.getAllCommunicationLogsOnTaskflow(taskflow);
		return CommunicationLogs[0] ? this.userService.user(CommunicationLogs[0].userId)?.name : this.translate.transform('general.na')
	}

	getStatus(taskflow: TaskflowModel) {
		let taskflowCommunicationLogs = this.getAllCommunicationLogsOnTaskflow(taskflow);
		return taskflowCommunicationLogs[0] && taskflowCommunicationLogs[0].statusType ? 'communicationlog.status.' + taskflowCommunicationLogs[0].statusType : 'general.na';
	}

	private getAllCommunicationLogsOnTaskflow(taskflow: TaskflowModel): CommunicationLogEntryModel[] {
		let communicationLog: CommunicationLogEntryModel[] = [];

		for (let taskAction of taskflow.taskActions) {
			communicationLog = communicationLog.concat(taskAction.communicationLogEntries);
		}
		communicationLog.sort((a, b) => (b.timestamp ? new Date(b.timestamp).getTime() : new Date(0).getTime()) - (a.timestamp ? new Date(a.timestamp).getTime() : new Date(0).getTime()));
		return communicationLog;
	}

	ngOnDestroy() {
		this.onTaskflowChanges.unsubscribe();
	}
}
